import { CommonModule } from "@angular/common";
import { Component, inject, signal } from "@angular/core";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { QuizService } from "src/app/services/api/quiz.service";
import { QuizAttempt } from "src/app/services/model/quiz.model";
import { SharedModule } from "src/shared/shared.module";
import { PipesModule } from "src/utils/pipes/pipes.module";

/**
 * Every quiz this student has finished.
 *
 * Replaces the legacy "examination record" tab. Each row links to its own
 * result, where the answer key and the per-topic breakdown live — this screen
 * deliberately stays a list, because its job is "find the one I want", not
 * "explain what happened".
 */
@Component({
  selector: "app-quiz-record",
  imports: [SharedModule, CommonModule, RouterModule, TranslateModule, PipesModule],
  templateUrl: "./quiz-record.component.html",
})
export class QuizRecordComponent {
  #quizService = inject(QuizService);

  #route = inject(ActivatedRoute);

  isLoading = signal(true);
  attempts = signal<QuizAttempt[]>([]);

  /**
   * Whose history this is.
   *
   * A student reading their own leaves this unset. A parent must name the
   * child — the API refuses to guess, for the same reason the skill report
   * does: a parent of two who omits it would be shown the wrong child's work
   * and would have no way to tell.
   */
  studentId = signal<number | null>(null);
  /** Read-only framing when a parent is looking at somebody else's work. */
  viewingOther = signal(false);

  ngOnInit(): void {
    // Subscribed, not snapshotted: the child selector on the monitor screen
    // stays on this route, so a one-shot read would leave the first child's
    // history on screen under the second child's name.
    this.#route.queryParamMap.subscribe((params) => {
      const raw = params.get("student_id");
      const id = raw ? Number(raw) : null;
      this.studentId.set(Number.isFinite(id as number) ? id : null);
      this.viewingOther.set(this.studentId() !== null);
      this.load();
    });
  }

  private load(): void {
    this.isLoading.set(true);
    const filter: any = { limit: 50, order: "quiz_attempts.id desc" };
    if (this.studentId() !== null) filter.student_id = this.studentId();

    this.#quizService.getAttempts(filter).subscribe({
      next: (res: any) => {
        // An attempt still in progress has no score to show; it belongs on the
        // quiz list as "resume", not here as a result.
        this.attempts.set((res?.data ?? []).filter((a: QuizAttempt) => a.status === "submitted"));
        this.isLoading.set(false);
      },
      error: () => {
        this.attempts.set([]);
        this.isLoading.set(false);
      },
    });
  }
}
